import { useState } from "react";
import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface BrainProps {
  id: string;
  name: string;
  content: string;
  enabled: boolean;
  avatarId: string;
  createdAt: string;
  updatedAt: string;
}

interface AvatarProps {
  id: string;
  displayName: string;
}

interface BrainUpdateProps {
  brain: BrainProps;
  avatars: AvatarProps[];
}

// #STYLE_GUIDE: Update component for editing existing records (includes delete functionality)
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
// #TODO: This is a basic implementation - customize form fields and validation as needed
export default function BrainUpdate({ brain, avatars }: BrainUpdateProps) {
  const [formData, setFormData] = useState({
    name: brain.name || "",
    content: brain.content || "",
    enabled: brain.enabled || false,
    avatarId: brain.avatarId || "",
  });

  const [selectedAvatar, setSelectedAvatar] = useState<AvatarProps | null>(
    avatars.find((avatar) => avatar.id === brain.avatarId) || null
  );
  const [avatarQuery, setAvatarQuery] = useState("");

  const { loading, errors, makeRequest, getFieldError } = useApiRequest({
    onSuccess: (data) => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit(`/brains/${data.id}`);
    },
    onError: (error) => {
      console.error("Failed to update brain:", error);
    },
  });

  const { loading: deleteLoading, makeRequest: makeDeleteRequest } =
    useApiRequest({
      onSuccess: () => {
        // #TURBO: Use Turbo.visit for navigation instead of window.location
        visit(`/avatars/${brain.avatarId}`);
      },
      onError: (error) => {
        console.error("Failed to delete brain:", error);
      },
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      avatarId: selectedAvatar?.id || "",
    };

    await makeRequest("PATCH", `/api/v1/brains/${brain.id}`, {
      brain: submitData,
    });
  };

  const handleDelete = async () => {
    if (
      confirm(
        "Are you sure you want to delete this brain? This action cannot be undone."
      )
    ) {
      await makeDeleteRequest("DELETE", `/api/v1/brains/${brain.id}`);
    }
  };

  const handleCancel = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/brains/${brain.id}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Edit Brain</h1>
        <div className="join">
          <button onClick={handleCancel} className="btn btn-outline join-item">
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn btn-error join-item"
            disabled={deleteLoading || loading}
          >
            {deleteLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                type="text"
                className={`input input-bordered ${
                  getFieldError("name") ? "input-error" : ""
                }`}
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
              />
              {getFieldError("name") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("name")}
                  </span>
                </label>
              )}
            </div>

            {/* Content field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Content</span>
              </label>
              <textarea
                className={`textarea textarea-bordered w-full ${
                  getFieldError("content") ? "textarea-error" : ""
                }`}
                placeholder="Enter content"
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                rows={4}
              />
              {getFieldError("content") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("content")}
                  </span>
                </label>
              )}
            </div>

            {/* Enabled field */}
            <div className="form-control">
              <label className="label cursor-pointer">
                <span className="label-text">Enabled</span>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={formData.enabled}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                />
              </label>
              {getFieldError("enabled") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("enabled")}
                  </span>
                </label>
              )}
            </div>

            {/* Avatar field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Avatar</span>
              </label>
              <div className="relative">
                <Listbox
                  value={selectedAvatar}
                  onChange={(value) => {
                    setSelectedAvatar(value);
                    setFormData((prev) => ({
                      ...prev,
                      avatarId: value?.id || "",
                    }));
                  }}
                >
                  <ListboxButton
                    className={`input input-bordered w-full text-left ${
                      getFieldError("avatarId") ? "input-error" : ""
                    }`}
                  >
                    {selectedAvatar?.displayName || "Select avatar"}
                    <ChevronDown className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none w-5 h-5" />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 w-full bg-base-100 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {(() => {
                      const filteredAvatar = avatars.filter((avatar) =>
                        avatar.displayName
                          .toLowerCase()
                          .includes(avatarQuery.toLowerCase())
                      );

                      if (filteredAvatar.length === 0) {
                        return (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No avatar found.
                          </div>
                        );
                      }

                      return filteredAvatar.map((avatar) => (
                        <ListboxOption
                          key={avatar.id}
                          value={avatar}
                          className="relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-base-200"
                        >
                          {avatar.displayName}
                          {selectedAvatar?.id === avatar.id && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </ListboxOption>
                      ));
                    })()}
                  </ListboxOptions>
                </Listbox>
              </div>
              {getFieldError("avatarId") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("avatarId")}
                  </span>
                </label>
              )}
            </div>

            {/* Submit buttons */}
            <div className="form-control mt-6">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading || deleteLoading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Updating...
                    </>
                  ) : (
                    "Update Brain"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  disabled={loading || deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
